import { Folder, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { memo, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { FileExplorerItem, DragDropHandlers } from '@features/file-explorer/types'
import DateDisplay from './DateDisplay'
import MimeTypeIcon from './MimeTypeBadge'
import UserAvatar from '@components/UserAvatar'
import AvatarStack from '@components/AvatarStack'
import ItemActionsMenu from '@features/file-explorer/components/ItemActionsMenu'
import ActionMenuItems from '@features/file-explorer/components/ActionMenuItems'
import { useContextMenu } from '@hooks/useContextMenu'
import { useItemSelection } from '@hooks/useItemSelection'
import { useItemDrag } from '@features/file-operations/hooks/useItemDrag'
import { createItemClickHandler } from '@utils/itemClickHandlers'

interface TableRowProps {
  item: FileExplorerItem
  items: FileExplorerItem[]
  index: number
  onItemClick: (item: FileExplorerItem) => void
  onSelectItem: (item: FileExplorerItem, index: number, shiftKey: boolean) => void
  onDelete: (item: FileExplorerItem) => void
  onRename: (item: FileExplorerItem) => void
  onDownload: (item: FileExplorerItem) => void
  onShow: (item: FileExplorerItem) => void
  onManagePermissions: (item: FileExplorerItem) => void
  dragAndDrop: DragDropHandlers
  getItemKey: (item: FileExplorerItem) => string
}

function TableRow({
  item,
  items,
  index,
  onItemClick,
  onSelectItem,
  onDelete,
  onRename,
  onDownload,
  onShow,
  onManagePermissions,
  dragAndDrop,
  getItemKey
}: TableRowProps) {
  const { t } = useTranslation()

  // Use shared hooks
  const { contextMenu, contextMenuRef, handleContextMenu, closeContextMenu } = useContextMenu()
  const { itemKey: _itemKey, isSelected } = useItemSelection({ item, getItemKey })
  const { handleDragStart, isDraggingThisItem, isPartOfDrag, dragCount, folderDragHandlers } = useItemDrag({
    item,
    items,
    getItemKey,
    dragAndDrop
  })

  // Memoize file size formatting
  const formattedSize = useMemo(() => {
    const bytes = item.size
    const isFolder = item.is_folder

    if (isFolder || bytes === null || bytes === 0) return '-'

    const k = 1024
    const sizeUnits = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const size = Math.round((bytes / Math.pow(k, i)) * 100) / 100

    return `${size} ${t(`fileExplorer.filters.units.${sizeUnits[i]}`)}`
  }, [item.size, item.is_folder, t])

  // Memoize file icon rendering
  const fileIcon = useMemo(() => {
    const thumbnailClasses = "w-[40px] h-[58px] rounded"

    if (item.is_folder) {
      return (
        <div className={`flex items-center justify-center ${thumbnailClasses}`}>
          <Folder className="w-10 h-10 text-primary" size={40} />
        </div>
      )
    }

    // If there's a preview URL, show the thumbnail
    if (item.preview_url) {
      return (
        <img
          src={item.preview_url}
          alt={item.name}
          className={`object-cover shadow-sm ${thumbnailClasses}`}
        />
      )
    }

    // Fallback to icon for documents without preview
    return (
      <div className={`flex items-center justify-center bg-base-200 ${thumbnailClasses}`}>
        <FileText className="w-10 h-10 text-base-content/70" />
      </div>
    )
  }, [item.is_folder, item.preview_url, item.name])

  const handleRowClick = (e: React.MouseEvent) => {
    // Only select if not clicking on the name button
    if ((e.target as HTMLElement).closest('button')) {
      return
    }
    onSelectItem(item, index, e.shiftKey)
  }


  return (
    <>
    <tr
      draggable={true}
      className={`hover:bg-base-200 cursor-pointer transition-colors border-l-4 ${
        isSelected ? 'bg-primary/5 border-l-primary' : 'border-l-transparent'
      } ${folderDragHandlers.isDragOverThisFolder ? 'bg-primary/20 ring-2 ring-primary' : ''} ${
        isPartOfDrag ? 'opacity-50' : ''
      }`}
      onClick={handleRowClick}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      onDragEnd={dragAndDrop.handleItemDragEnd}
      onDragEnter={folderDragHandlers.handleDragEnter}
      onDragOver={folderDragHandlers.handleDragOver}
      onDragLeave={folderDragHandlers.handleDragLeave}
      onDrop={folderDragHandlers.handleDrop}
    >
      <td className='p-0'>
        <a
          href={item.is_folder ? `/${item.id}` : `/documents/${item.id}/preview`}
          className="link link-hover font-medium text-left flex items-center gap-2 pr-8 relative w-fit"
          onClick={createItemClickHandler(item, onItemClick)}
        >
          {fileIcon}
          <div className="flex flex-col gap-0.5">
            <span>{item.name}</span>
            {item.description && (
              <span className="text-xs text-base-content/40 font-normal">
                {item.description}
              </span>
            )}
          </div>
          {/* Show count badge when dragging multiple items and this is the first one */}
          {isDraggingThisItem && dragCount > 1 && (
            <div className="absolute -top-2 -right-2 badge badge-primary badge-sm">
              +{dragCount - 1}
            </div>
          )}
        </a>
      </td>
      <td>
        {item.owner && <UserAvatar user={item.owner} size="sm" />}
      </td>
      <td>
        <AvatarStack users={item.shared_with} maxVisible={3} size="sm" />
      </td>
      <td className="text-sm">
        <div className="flex items-center gap-2">
          {item.type === 'Document' && item.mime_type && (
            <MimeTypeIcon mimeType={item.mime_type} extension={item.extension} />
          )}
          <span>{formattedSize}</span>
        </div>
      </td>
      <td className="text-sm">
        <DateDisplay createdAt={item.created_at} updatedAt={item.updated_at} />
      </td>
      <td>
        <ItemActionsMenu item={item} onDelete={onDelete} onRename={onRename} onDownload={onDownload} onShow={onShow} onManagePermissions={onManagePermissions} />
      </td>
    </tr>

    {/* Context Menu - rendered via portal to avoid invalid HTML structure */}
    {contextMenu && createPortal(
      <div
        ref={contextMenuRef}
        className="fixed bg-base-100 rounded-box z-50 w-52 p-2 shadow-lg border border-base-300"
        style={{
          left: `${contextMenu.x}px`,
          top: `${contextMenu.y}px`,
        }}
      >
        <ul className="menu menu-sm p-0">
          <ActionMenuItems
            item={item}
            onDelete={onDelete}
            onRename={onRename}
            onDownload={onDownload}
            onShow={onShow}
            onManagePermissions={onManagePermissions}
            onActionClick={closeContextMenu}
          />
        </ul>
      </div>,
      document.body
    )}
    </>
  )
}

// Custom comparison function for memo - only re-render if these props changed
// Note: isSelected is no longer a prop, it's subscribed from store
function arePropsEqual(prevProps: TableRowProps, nextProps: TableRowProps) {
  // Check if shared_with has changed
  const prevSharedWith = prevProps.item.shared_with
  const nextSharedWith = nextProps.item.shared_with
  const sharedWithChanged =
    prevSharedWith.length !== nextSharedWith.length ||
    prevSharedWith.some((user, i) => user.id !== nextSharedWith[i]?.id)

  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.item.updated_at === nextProps.item.updated_at &&
    !sharedWithChanged &&
    prevProps.dragAndDrop.isDragging === nextProps.dragAndDrop.isDragging &&
    prevProps.dragAndDrop.dragOverTarget === nextProps.dragAndDrop.dragOverTarget &&
    prevProps.dragAndDrop.draggedItem?.id === nextProps.dragAndDrop.draggedItem?.id
  )
}

export default memo(TableRow, arePropsEqual)
